import React, { useMemo } from "react";

import {
  FieldConfig,
  FieldOption,
  FormFieldType,
} from "../../../api/formConfigs";

import TextCell from "../../../components/ui/baseDataTable/cells/TextCell";
import NumberCell from "../../../components/ui/baseDataTable/cells/NumberCell";
import DateCell from "../../../components/ui/baseDataTable/cells/DateCell";
import MultiselectCell from "../../../components/ui/baseDataTable/cells/MultiselectCell";
import SelectDropdownV2 from "../../../../src/components/ui/selectdropdown/v2";

import { OptionNameValuePair } from "../../../lib/interfaces/types";

const RADIO_GRID_COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const radioGridColsClass = (optionCount: number): string =>
  RADIO_GRID_COLS[optionCount >= 1 && optionCount <= 4 ? optionCount : 1];

interface DynamicFormFieldProps {
  field: FieldConfig;
  value: any;
  onChange(value: any): void;
}

const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  value,
  onChange,
}) => {
  const mappedOptions = useMemo<OptionNameValuePair<string>[]>(
    () =>
      field.options?.map((option: FieldOption) => ({
        name: option.label,
        value: String(option.value),
      })) ?? [],
    [field.options],
  );

  switch (field.field_type) {
    case FormFieldType.TEXT: {
      return (
        <TextCell
          value={typeof value === "string" ? value : ""}
          isEditable
          numberOfRows={1}
          enableTooltip={false}
          onChange={onChange}
          containerClassName="w-full h-[36px] rounded-[8px] border border-[#E5E5E5] bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] flex items-center overflow-hidden focus-within:bg-[#F3F4F6]"
          textareaClassName="px-3 py-1.5 w-full h-full text-sm text-[#0A0A0A] bg-transparent outline-none border-none resize-none focus:bg-transparent"
        />
      );
    }

    case FormFieldType.TEXT_AREA: {
      return (
        <TextCell
          value={typeof value === "string" ? value : ""}
          isEditable
          numberOfRows={4}
          enableTooltip={false}
          enableScroll={true} // Safe scroll handler
          onChange={onChange}
          containerClassName="w-full rounded-[8px] border border-[#E5E5E5] bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] flex items-center overflow-hidden focus-within:bg-[#F3F4F6]"
          textareaClassName="p-3 w-full text-sm text-[#0A0A0A] bg-transparent outline-none border-none resize-none focus:bg-transparent"
        />
      );
    }

    case FormFieldType.INT:
    case FormFieldType.FLOAT: {
      return (
        <NumberCell
          value={typeof value === "number" ? value : undefined}
          isEditable
          enableDefaultStyles={false}
          onChange={(updatedValue) => {
            if (updatedValue == null) {
              onChange(updatedValue);
              return;
            }

            if (field.field_type === FormFieldType.INT) {
              onChange(Math.floor(updatedValue));
              return;
            }

            onChange(updatedValue);
          }}
          inputClassName="w-full h-[36px] px-3 py-1.5 text-sm border border-[#E5E5E5] rounded-[8px] bg-white hover:bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] focus:bg-[#F3F4F6] focus:border-[#E5E5E5] outline-none"
        />
      );
    }

    case FormFieldType.DATE: {
      return (
        <DateCell
          value={typeof value === "string" ? value : ""}
          isEditable
          onChange={onChange}
          inputClassName="w-full h-[36px] px-3 py-1.5 text-sm border border-[#E5E5E5] rounded-[8px] bg-white hover:bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] focus:bg-[#F3F4F6] focus:outline-none"
        />
      );
    }

    case FormFieldType.SELECT: {
      const selectedOption = mappedOptions.find((opt) => opt.value === value);
      return (
        <SelectDropdownV2
          name={field.label}
          placeholder="Select option"
          options={mappedOptions}
          selectedOption={selectedOption}
          onSelectionChange={(selected) => onChange(selected.value)}
          containerClassName="w-full"
          labelClassName="w-full h-[36px] px-3 py-1.5 flex items-center justify-between border border-[#E5E5E5] bg-white hover:bg-white rounded-[8px] text-sm text-[#0A0A0A] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] focus-within:bg-[#F3F4F6]"
          dropdownClassName="w-max min-w-full"
        />
      );
    }

    case FormFieldType.MULTISELECT: {
      return (
        <MultiselectCell
          name={field.label}
          value={Array.isArray(value) ? (value as string[]) : []}
          isEditable
          options={mappedOptions}
          onChange={onChange}
          className="w-full h-[36px] px-3 py-1.5 text-sm text-[#0A0A0A] hover:bg-white"
          rootContainerClassName="w-full flex items-center rounded-[8px] border border-[#E5E5E5] bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] focus-within:bg-[#F3F4F6]"
          dropdownClassName="w-max min-w-full"
          chipClassName="bg-[#EBEEFE] border-none text-[#304ADD] px-2 py-[2px] rounded-md text-xs font-medium"
        />
      );
    }

    case FormFieldType.RADIOBUTTON: {
      return (
        <div className="rounded-[8px] border border-[#E5E5E5] bg-white min-h-[36px] px-3 py-1.5 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] w-full">
          <div className={`grid gap-x-4 gap-y-2 ${radioGridColsClass(field.options?.length ?? 1)}`}>
            {field.options?.map((option) => (
              <label
                key={String(option.value)}
                title={option.label}
                className="flex items-center gap-2 cursor-pointer select-none min-w-0"
              >
                <input
                  type="radio"
                  name={field.key}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                  className="h-4 w-4 shrink-0 cursor-pointer border-gray-300 accent-[#3551F3] focus:ring-[#3551F3]"
                />
                <span className="text-sm text-[#0A0A0A] truncate">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    case FormFieldType.CHECKBOX: {
      return (
        <div className="rounded-[8px] border border-[#E5E5E5] bg-white min-h-[36px] flex items-center px-3 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] w-full">
          <label className="flex items-center gap-2.5 cursor-pointer select-none w-full">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(event) => onChange(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#3551F3] focus:ring-[#3551F3]"
            />
            <span className="text-sm text-[#0A0A0A]">Yes</span>
          </label>
        </div>
      );
    }

    default:
      return null;
  }
};

export default DynamicFormField;